import { CheckCircle2 } from 'lucide-react';
import useInView from '../hooks/useInView';

const POINTS = [
  {
    num: '01',
    title: 'Execution-led hiring, not CV forwarding',
    description: 'We own the process end-to-end. Every submission is deliberate, every candidate vetted for skills, motivation, and cultural fit.',
  },
  {
    num: '02',
    title: 'Founder and CXO-level alignment',
    description: "Our team brings direct experience working with and for Founders and CXOs. We speak the same language as your leadership team.",
  },
  {
    num: '03',
    title: 'Candidate intelligence reports',
    description: 'Beyond the resume — motivation drivers, risk flags, salary expectations, and a clear rationale for every candidate we submit.',
  },
  {
    num: '04',
    title: 'Full funnel hiring insights',
    description: 'We identify where your hiring process is losing candidates and help fix it. Conversion rates, bottlenecks, offer dynamics — tracked.',
  },
  {
    num: '05',
    title: 'Speed without sacrificing quality',
    description: 'Deep market coverage means first submissions within days — with quality built in, not bolted on after the fact.',
  },
];

export default function DifferentiatorsSection() {
  const [ref, inView] = useInView();

  return (
    <section id="differentiators" className="py-24 bg-[#F9FAFB]" data-testid="differentiators-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Section header */}
        <div className="grid lg:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[#E8500A]" />
              <span
                className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.22em]"
                style={{ fontFamily: '"Inter",sans-serif' }}
              >
                Why Choose Us
              </span>
            </div>
            <h2
              className="text-4xl sm:text-5xl font-black text-[#0A1628] leading-none tracking-tight"
              style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              data-testid="differentiators-heading"
            >
              The IndUS<br />Difference.
            </h2>
          </div>
          <p className="text-[#4B5563] text-base leading-relaxed lg:pb-2" style={{ fontFamily: '"Inter",sans-serif' }}>
            We're not a resume pipeline. We're a strategic hiring partner that embeds itself in your process and improves it — from the intake meeting to the accepted offer.
          </p>
        </div>

        {/* 5 points grid */}
        <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/8">
          {POINTS.map((pt, i) => (
            <div
              key={i}
              className="bg-[#F9FAFB] hover:bg-white p-8 group transition-all duration-300"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(20px)',
                transition: `all 0.6s ease ${i * 100}ms`,
              }}
              data-testid={`diff-point-${i}`}
            >
              {/* Number accent */}
              <div
                className="text-4xl font-black text-[#E8500A]/20 group-hover:text-[#E8500A]/40 transition-colors duration-300 leading-none mb-4"
                style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              >
                {pt.num}
              </div>

              <div className="flex gap-3">
                <CheckCircle2 size={16} className="text-[#E8500A] flex-shrink-0 mt-0.5" />
                <div>
                  <div
                    className="text-sm font-bold text-[#0A1628] mb-2 leading-snug"
                    style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
                  >
                    {pt.title}
                  </div>
                  <div className="text-sm text-[#4B5563] leading-relaxed" style={{ fontFamily: '"Inter",sans-serif' }}>
                    {pt.description}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
